import React, { useRef, useCallback } from "react";
import { Card, CardBody, Row, Col, Table, Badge } from "reactstrap";

/**
 * Drag-to-scroll wrapper for table containers
 */
const DragScrollDiv = ({ children, style, ...props }) => {
  const ref = useRef(null);
  const drag = useRef({ active: false, sx: 0, sy: 0, sl: 0, st: 0 });

  const onDown = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { active: true, sx: e.pageX, sy: e.pageY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  }, []);

  const onMove = useCallback((e) => {
    if (!drag.current.active) return;
    e.preventDefault();
    const el = ref.current;
    if (!el) return;
    el.scrollLeft = drag.current.sl - (e.pageX - drag.current.sx);
    el.scrollTop = drag.current.st - (e.pageY - drag.current.sy);
  }, []);

  const onUp = useCallback(() => {
    drag.current.active = false;
    if (ref.current) {
      ref.current.style.cursor = "grab";
      ref.current.style.userSelect = "";
    }
  }, []);

  return (
    <div
      ref={ref}
      style={{ ...style, cursor: "grab" }}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * Renders structured AI response data (metric cards, tables)
 */
const ToolResultCard = ({ data }) => {
  if (!data) return null;

  return (
    <div style={{ marginTop: 8 }}>
      {/* Metric Cards */}
      {data.cards && data.cards.length > 0 && (
        <Row className="no-gutters" style={{ margin: "0 -3px" }}>
          {data.cards.map((card, i) => (
            <Col key={i} xs="6" md={data.cards.length > 3 ? "4" : "4"} style={{ padding: "0 3px", marginBottom: 6 }}>
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: 12 }}
              >
                <CardBody className="p-2 text-center">
                  <div
                    className="text-muted mb-1"
                    style={{ fontSize: 11, fontWeight: 500 }}
                  >
                    {card.title}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: card.color === "danger"
                        ? "#dc3545"
                        : card.color === "warning"
                          ? "#ffc107"
                          : card.color === "success"
                            ? "#28a745"
                            : "#2d3436",
                    }}
                  >
                    {card.formatted || card.value}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Tables */}
      {data.tables && data.tables.length > 0 && (
        <div>
          {data.tables.map((table, i) => (
            <Card
              key={i}
              className="shadow-sm border-0 mb-2"
              style={{ borderRadius: 12 }}
            >
              <CardBody className="p-2">
                {table.title && (
                  <div
                    className="font-weight-bold mb-2"
                    style={{ fontSize: 13 }}
                  >
                    {table.title}
                  </div>
                )}
                <DragScrollDiv style={{ overflowX: "auto", overflowY: "auto", maxHeight: 240 }}>
                  <Table
                    size="sm"
                    striped
                    borderless
                    style={{ fontSize: 12, marginBottom: 0 }}
                  >
                    <thead>
                      <tr>
                        {table.headers.map((h, j) => (
                          <th key={j} style={{ whiteSpace: "nowrap" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row, j) => (
                        <tr key={j}>
                          {row.map((cell, k) => (
                            <td key={k} style={{ whiteSpace: "nowrap" }}>
                              {cell === "Hết hàng" ? (
                                <Badge color="danger" pill>
                                  {cell}
                                </Badge>
                              ) : cell === "Sắp hết" ? (
                                <Badge color="warning" pill>
                                  {cell}
                                </Badge>
                              ) : (
                                cell
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </DragScrollDiv>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToolResultCard;
